import axios from "axios";
import http from "../utility/http";
import store from "../redux/store";

/**
 * The shared http utility always sends JSON and reads a JSON envelope, so multipart uploads and
 * PDF downloads go through axios directly. They still use the configured API server and the
 * access token from the store, so they behave like every other authenticated call.
 */
const authorizedRequest = (config) => {
  const token = store.getState()?.currentUser?.tokens?.accessToken;
  return axios({
    baseURL: `https://portal-api.transfermyrx.com`,
    withCredentials: true,
    ...config,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(config.headers || {}),
    },
  });
};

const GetByRequestId = async (requestId) => {
  return await http.get(`/v1/transfer/transfer-request/${requestId}/follow-ups`);
};

const Create = async (requestId, note, attachments = []) => {
  return await http.post(`/v1/transfer/transfer-request/${requestId}/follow-ups`, {
    note,
    attachments,
  });
};

const Update = async (followUpId, note) => {
  return await http.patch(`/v1/rx-follow-up/${followUpId}`, { note });
};

const Delete = async (followUpId) => {
  return await http.delete(`/v1/rx-follow-up/${followUpId}`, {});
};

/** Uploads one image or PDF and returns the metadata to send back when saving the note. */
const UploadAttachment = async (file) => {
  const formData = new FormData();
  formData.append("file", file);

  const response = await authorizedRequest({
    method: "post",
    url: "/v1/rx-follow-up/upload",
    data: formData,
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response?.data?.data?.data;
};

const triggerDownload = (blob, fileName) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

const DownloadRequestPdf = async (requestId) => {
  const response = await authorizedRequest({
    method: "get",
    url: `/v1/transfer/transfer-request/${requestId}/follow-ups/pdf`,
    responseType: "blob",
  });
  triggerDownload(response.data, `rx-follow-up-${requestId}.pdf`);
};

/** Terms are Rx numbers, transfer request numbers, or a mix of both. */
const SearchReport = async (terms) => {
  return await http.get(
    `/v1/reports/rx-follow-up/search?q=${encodeURIComponent(terms.join(","))}`
  );
};

const DownloadReportPdf = async (requestIds) => {
  const response = await authorizedRequest({
    method: "post",
    url: "/v1/reports/rx-follow-up/pdf",
    data: { requestIds },
    responseType: "blob",
    headers: { "Content-Type": "application/json" },
  });
  const stamp = new Date().toISOString().slice(0, 10);
  triggerDownload(response.data, `rx-follow-up-report-${stamp}.pdf`);
};

const api = {
  GetByRequestId,
  Create,
  Update,
  Delete,
  UploadAttachment,
  DownloadRequestPdf,
  SearchReport,
  DownloadReportPdf,
};

export default api;
